import { z } from 'zod';
import { type UuidString, isUuid } from '../ids/index.js';

const COMMAND_TYPE_RE = /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)+$/;

/**
 * The wire envelope every station command travels in (plan §9.3). `command_id` is the
 * idempotency key; `payload` is validated separately by the command's own schema.
 */
export const commandEnvelope = z.object({
  /** UUIDv7 made on the device. */
  command_id: z.uuid(),
  /** Dotted command type, e.g. `production.complete`. */
  type: z.string().regex(COMMAND_TYPE_RE, 'Command types look like "area.action"'),
  /** Payload schema version (server accepts current and previous). */
  schema_version: z.int().positive(),
  payload: z.unknown(),
  device_id: z.uuid(),
  staff_id: z.uuid(),
  /** Monotonic per device, starting at 1. */
  device_seq: z.int().min(1),
  /** Device clock, ISO-8601 with offset. */
  occurred_at: z.iso.datetime({ offset: true }),
  app_version: z.string().min(1),
});

/** A parsed command envelope. */
export type CommandEnvelope = z.infer<typeof commandEnvelope>;

const FLAG_CODE_RE = /^[a-z][a-z0-9_]*$/;

/**
 * Something a person should review. Physical facts are applied **with flags** rather than
 * rejected (e.g. `negative_stock`, `performed_after_permission_revoked`, `clock_skew`).
 */
export const flagSchema = z.object({
  /** snake_case code. */
  code: z.string().regex(FLAG_CODE_RE),
  /** Human-readable explanation. */
  message: z.string().optional(),
  /** Structured context (ids, quantities as strings). */
  data: z.record(z.string(), z.unknown()).optional(),
});

/** A review flag. */
export type Flag = z.infer<typeof flagSchema>;

/**
 * Final outcome statuses of applying a command: the only statuses stored in `command_log`.
 * Transient answers (`retry_later`, …) are in `./response.ts`.
 */
export const COMMAND_STATUSES = ['applied', 'applied_with_flags', 'rejected'] as const;
/** One of {@link COMMAND_STATUSES}. */
export type CommandStatus = (typeof COMMAND_STATUSES)[number];

/** The stored/returned result of a command. `applied_with_flags` always has ≥ 1 flag. */
export const commandResultSchema = z
  .object({
    status: z.enum(COMMAND_STATUSES),
    flags: z.array(flagSchema),
    reason: z.string().optional(),
  })
  .refine((r) => r.status !== 'applied_with_flags' || r.flags.length > 0, {
    message: 'applied_with_flags needs at least one flag',
    path: ['flags'],
  })
  .refine((r) => r.status !== 'rejected' || (r.reason ?? '') !== '', {
    message: 'rejected needs a reason',
    path: ['reason'],
  });

/** Result of applying a command. */
export type CommandResult = z.infer<typeof commandResultSchema>;

/** `applied`, or `applied_with_flags` when any flags are given. */
export function applied(flags: readonly Flag[] = []): CommandResult {
  return flags.length > 0
    ? { status: 'applied_with_flags', flags: [...flags] }
    : { status: 'applied', flags: [] };
}

/** `rejected` with a reason (only for malformed or unauthorised non-physical commands). */
export function rejected(reason: string, flags: readonly Flag[] = []): CommandResult {
  if (reason === '') throw new RangeError('A rejection needs a reason');
  return { status: 'rejected', flags: [...flags], reason };
}

/**
 * Idempotency key for the `n`-th ledger movement a command writes: `cmd:<id>:<n>`.
 * The same command applied twice yields the same keys, so no duplicate movements.
 *
 * Uniqueness is always **per account**: every table with an `idempotency_key` declares
 * `UNIQUE (account_id, idempotency_key)`, never a global UNIQUE, so one tenant's keys can
 * neither collide with nor reveal another's.
 */
export function movementKey(commandId: string, n: number): string {
  if (!z.uuid().safeParse(commandId).success) throw new RangeError('commandId must be a UUID');
  if (!Number.isSafeInteger(n) || n < 0) throw new RangeError('n must be an integer >= 0');
  return `cmd:${commandId}:${n}`;
}

// Components are percent-encoded so a ':' inside an external id can't forge another key.
const keyPart = (value: string, name: string): string => {
  if (value === '') throw new RangeError(`${name} must not be empty`);
  return encodeURIComponent(value);
};

/**
 * Idempotency key for a movement derived from an external sale line:
 * `sale:<channel id>:<external_id>:<line_ref>:v<revision>`. The channel is the `channel`
 * row's **id** (a UUID), never its kind or name: two channels of the same kind must not
 * share keys. The other components are percent-encoded (plain ids pass through unchanged).
 *
 * Uniqueness is always **per account** (`UNIQUE (account_id, idempotency_key)`), like
 * {@link movementKey}.
 */
export function saleKey(
  channelId: UuidString,
  externalId: string,
  lineRef: string,
  revision: number,
): string {
  if (!isUuid(channelId)) throw new RangeError('channelId must be a UUID (the channel row id)');
  if (!Number.isSafeInteger(revision) || revision < 0) {
    throw new RangeError('revision must be an integer >= 0');
  }
  return `sale:${channelId.toLowerCase()}:${keyPart(externalId, 'externalId')}:${keyPart(lineRef, 'lineRef')}:v${revision}`;
}
